import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Alert, 
  Form, 
  Input, 
  Typography, 
  Space, 
  Card, 
  Table,
  Modal,
  Select,
  Popconfirm,
  message,
  Tag
} from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import API from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const UserBlacklistManagement = () => {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  
  const [blacklistData, setBlacklistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false); // 添加提交loading状态
  const [userIPs, setUserIPs] = useState([]); // 用户拥有权限的IP列表
  const [selectedHostIP, setSelectedHostIP] = useState(''); // 当前查询的host_ip
  const [selectedTargetIPs, setSelectedTargetIPs] = useState([]); // 添加黑名单时选择的目标IP
  
  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // 搜索关键词
  const [keywords, setKeywords] = useState('');

  // 获取用户拥有权限的IP列表
  useEffect(() => {
    fetchUserIPs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserIPs = async () => {
    try {
      const response = await API.get('/api/user/assigned-ips');
      if (response.data.assignedIps) {
        // 处理IP数据结构，可能是字符串数组或对象数组
        const ips = response.data.assignedIps.map(item => {
          if (typeof item === 'object' && item !== null) {
            return { ip: item.ip || item };
          }
          return { ip: item };
        });
        setUserIPs(ips);
        // 默认选择第一个IP
        if (ips.length > 0) {
          const firstIP = ips[0].ip;
          setSelectedHostIP(firstIP);
          searchForm.setFieldsValue({ host_ip: firstIP });
        }
      }
    } catch (error) {
      console.error('获取IP列表失败:', error);
      message.error('获取IP列表失败');
    }
  };

  // 获取黑名单列表
  const fetchBlacklist = async (page = 1, limit = 20, searchKeywords = '') => {
    if (!selectedHostIP) {
      message.warning('请先选择要查询的IP地址');
      return;
    }

    setLoading(true);
    try {
      const response = await API.get('/api/user/static-blacklist', {
        params: {
          host_ip: selectedHostIP,
          keywords: searchKeywords,
          page: page,
          limit: limit
        }
      });

      if (response.data.code === 0) {
        const data = response.data.data;
        setBlacklistData(data.list || []);
        setPagination({
          current: data.pagination?.page || page,
          pageSize: data.pagination?.limit || limit,
          total: data.pagination?.total || 0
        });
      } else {
        message.error(response.data.msg || '获取黑名单失败');
        setBlacklistData([]);
      }
    } catch (error) {
      console.error('获取黑名单失败:', error);
      message.error(error.response?.data?.msg || '获取黑名单失败');
      setBlacklistData([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    fetchBlacklist(pagination.current, pagination.pageSize, keywords);
  };

  // 处理搜索
  const handleSearch = () => {
    const searchKeywords = searchForm.getFieldValue('keywords') || '';
    setKeywords(searchKeywords);
    fetchBlacklist(1, pagination.pageSize, searchKeywords);
  };

  // 切换host_ip时重新查询
  const handleHostIPChange = (value) => {
    setSelectedHostIP(value);
    setKeywords('');
    searchForm.setFieldsValue({ keywords: '' });
  };

  // 查询按钮点击
  useEffect(() => {
    if (selectedHostIP) {
      fetchBlacklist(1, pagination.pageSize, keywords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHostIP]);

  // 显示添加模态框
  const showAddModal = () => {
    form.resetFields();
    setSelectedTargetIPs([]);
    setIsModalVisible(true);
  };

  // 处理添加黑名单
  const handleAddBlacklist = async () => {
    try {
      const values = await form.validateFields();
      
      if (selectedTargetIPs.length === 0) {
        message.warning('请选择至少一个目标IP');
        return;
      }

      setSubmitLoading(true);
      
      const requestData = {
        type: values.type,
        start_ip: values.start_ip,
        end_ip: values.type === 1 ? values.end_ip : '',
        target_ips: selectedTargetIPs,
        remark: values.remark || ''
      };

      const response = await API.post('/api/user/static-blacklist', requestData);

      if (response.data.code === 0) {
        const addedCount = response.data.data?.added_count || 0;
        const totalRequested = response.data.data?.operation_result?.total_requested || selectedTargetIPs.length;
        
        // 关闭模态框
        setIsModalVisible(false);
        form.resetFields();
        setSelectedTargetIPs([]);
        
        // 显示成功消息
        if (addedCount === totalRequested) {
          message.success(`添加黑名单成功，共添加 ${addedCount} 条规则`);
        } else {
          message.warning(`部分成功：成功添加 ${addedCount}/${totalRequested} 条规则`);
        }
        
        // 刷新列表
        setTimeout(() => {
          fetchBlacklist(pagination.current, pagination.pageSize, keywords);
        }, 300);
      } else {
        message.error(response.data.msg || '添加黑名单失败');
      }
    } catch (error) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      console.error('添加黑名单失败:', error);
      message.error(error.response?.data?.msg || '添加黑名单失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 删除黑名单
  const handleDeleteBlacklist = async (record) => {
    try {
      const response = await API.delete(`/api/user/static-blacklist/${record.id}`, {
        params: { ip: selectedHostIP }
      });

      if (response.data.code === 0) {
        message.success('删除黑名单成功');
        fetchBlacklist(pagination.current, pagination.pageSize, keywords);
      } else {
        message.error(response.data.msg || '删除黑名单失败');
      }
    } catch (error) {
      console.error('删除黑名单失败:', error);
      message.error(error.response?.data?.msg || '删除黑名单失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 0 ? 'red' : 'orange'}>
          {type === 0 ? '单个IP' : 'IP段'}
        </Tag>
      ),
    },
    {
      title: '起始IP',
      dataIndex: 'start_ip',
      key: 'start_ip',
      width: 150,
    },
    {
      title: '结束IP',
      dataIndex: 'end_ip',
      key: 'end_ip',
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'create_time_formatted',
      key: 'create_time_formatted',
      width: 180,
      render: (text) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="确定要删除这条黑名单吗？"
          onConfirm={() => handleDeleteBlacklist(record)}
          okText="确定"
          cancelText="取消"
        >
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          >
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{
      maxWidth: '1400px',
      padding: '30px 24px',
      margin: '0 auto'
    }}>
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        {/* 标题和说明 */}
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ marginBottom: 16 }}>
            静态黑名单管理
          </Title>
          <Alert
            message="管理您的静态黑名单，支持添加单个IP或IP段"
            type="warning"
            showIcon
            style={{ maxWidth: 800, margin: '0 auto' }}
          />
        </div>

        {/* 搜索和操作区域 */}
        <Card>
          <Form
            form={searchForm}
            layout="inline"
            style={{ marginBottom: 16 }}
          >
            <Form.Item label="目标IP" name="host_ip" style={{ minWidth: 200 }}>
              <Select
                placeholder="选择要查询的IP"
                onChange={handleHostIPChange}
                style={{ minWidth: 200 }}
              >
                {userIPs.map(ipItem => (
                  <Option key={ipItem.ip} value={ipItem.ip}>
                    {ipItem.ip}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item label="关键词" name="keywords" style={{ minWidth: 200 }}>
              <Input 
                placeholder="搜索IP或备注" 
                allowClear
                onPressEnter={handleSearch}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchBlacklist(pagination.current, pagination.pageSize, keywords)}
                >
                  刷新
                </Button>
              </Space>
            </Form.Item>
          </Form>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddModal}
            danger
          >
            添加黑名单
          </Button>
        </Card>

        {/* 表格 */}
        <Card>
          <Table
            columns={columns}
            dataSource={blacklistData}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </Card>
      </Space>

      {/* 添加黑名单模态框 */}
      <Modal
        title="添加静态黑名单"
        open={isModalVisible}
        onOk={handleAddBlacklist}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setSelectedTargetIPs([]);
        }}
        width={600}
        okText="确定"
        cancelText="取消"
        confirmLoading={submitLoading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ type: 0 }}
        >
          <Form.Item
            label="类型"
            name="type"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="选择类型">
              <Option value={0}>单个IP</Option>
              <Option value={1}>IP段</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="起始IP"
            name="start_ip"
            rules={[
              { required: true, message: '请输入起始IP' },
              { 
                pattern: /^(\d{1,3}\.){3}\d{1,3}$/, 
                message: '请输入有效的IP地址' 
              }
            ]}
          >
            <Input placeholder="例如: 111.180.205.107" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.type !== currentValues.type
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('type') === 1 ? (
                <Form.Item
                  label="结束IP"
                  name="end_ip"
                  rules={[
                    { required: true, message: '请输入结束IP' },
                    { 
                      pattern: /^(\d{1,3}\.){3}\d{1,3}$/, 
                      message: '请输入有效的IP地址' 
                    }
                  ]}
                >
                  <Input placeholder="例如: 111.180.205.200" />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            label="目标IP（要添加黑名单的IP）"
            required
          >
            <Select
              mode="multiple"
              placeholder="选择一个或多个目标IP"
              value={selectedTargetIPs}
              onChange={setSelectedTargetIPs}
              style={{ width: '100%' }}
            >
              {userIPs.map(ipItem => (
                <Option key={ipItem.ip} value={ipItem.ip}>
                  {ipItem.ip}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 12 }}>
              黑名单将应用到这些IP上
            </Text>
          </Form.Item>

          <Form.Item
            label="备注"
            name="remark"
          >
            <Input.TextArea 
              placeholder="请输入备注信息（可选）" 
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserBlacklistManagement;

